import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  Input,
  QueryList,
  ViewChildren,
} from '@angular/core';
import {RecordTableColumnParams, RecordTotalData} from "../../../../../types/records-table.types";
import {RecordTableAction} from "../../../../../types/record-table-action.types";
import {ColumnParamsStateService} from "../../../../../services/table-services/column-params-state.service";
import {UtilityService} from "../../../../../services/utility.service";
import {ColumnResizeService} from "../../../../../services/table-services/column-resize.service";
import {RecordsTableService} from "../../../../../services/records-table.service";
import {totalMap} from "../../../configs/records-table-config";
import {TableContextService} from "../../../../../services/table-services/table-context.service";

@Component({
  selector: 'app-table-footer',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './table-footer.component.html',
  styleUrls: [
    '../../../records-table-view.component.scss',
    './table-footer.component.scss'
  ]
})
export class TableFooterComponent implements AfterViewInit {
  @Input({ required: true }) tableTotals: Array<RecordTotalData> = [];
  @Input({ required: true }) tableActions: Array<RecordTableAction> = [];
  @Input({ required: true }) resourceParamsKey: string = '';
  @Input() isAnyTableActionAvailable: boolean = false;
  @ViewChildren('footerCell') footerCells!: QueryList<ElementRef<HTMLElement>>;

  public columnParamsStateService: ColumnParamsStateService = inject(ColumnParamsStateService);
  private recordsTableService: RecordsTableService = inject(RecordsTableService);
  private utilityService: UtilityService = inject(UtilityService);
  private columnResizeService = inject(ColumnResizeService);
  private tableContextService: TableContextService = inject(TableContextService);

  public recordTotalValue: string = '';

  public ngAfterViewInit() {
    this.registerHeaderCells();
    this.footerCells.changes.subscribe(() => this.registerHeaderCells());
  }

  public isColumnDisplayed(columnParams: RecordTableColumnParams): boolean {
    return this.recordsTableService.isColumnDisplayed(columnParams);
  }

  public isNeedCheckbox(index: number): boolean {
    return this.tableActions.includes('CREATE_BLOGGER_GROUPS') && index === 0;
  }

  public getWidth(columnParam: RecordTableColumnParams): number {
    return this.recordsTableService.getColumnWidth(columnParam, this.tableContextService.getTableId());
  }

  public getColumnTotalParams(columnKey: string): RecordTotalData | null {
    const recordTotal = this.tableTotals.find((total: RecordTotalData) => total.columnName === columnKey) ?? null;
    if (!recordTotal) { return null; }

    if (recordTotal.value === null) { return null; }
    const columnProperties = this.columnParamsStateService.getColumnParamsByKey(this.resourceParamsKey)?.find(i => i.key === columnKey)?.columnProperties;

    if (columnProperties == null) { return recordTotal; }

    this.recordTotalValue = this.utilityService.formatNumberForDisplay({
      value: recordTotal.value,
      fractional: columnProperties.FRACTIONAL,
      numberSeparator: columnProperties.NUMBER_SEPARATOR
    });

    return recordTotal;
  }

  public getDescriptionRecordTotal(columnKey: string): string {
    const recordTotal = this.tableTotals.find((total: RecordTotalData) => total.columnName === columnKey) ?? null;
    if (!recordTotal) { return ''; }

    return recordTotal.description ?? '';
  }

  public getTranscriptRecordTotalType(columnKey: string): string | null {
    const recordTotal = this.tableTotals.find((total: RecordTotalData) => total.columnName === columnKey) ?? null;
    if (!recordTotal) { return null; }

    return this.getTranscriptionByType(recordTotal.type);
  }

  private registerHeaderCells() {
    this.footerCells.forEach(ref => {
      const key = ref.nativeElement.getAttribute('data-column')!;
      this.columnResizeService.registerElement(key, ref.nativeElement);
    });
  }

  private getTranscriptionByType(recordTotalType: string): string | null {
    return totalMap[recordTotalType] ?? null;
  }
}
